<template>
  <div class="sf-page">
    <confirm-dialog ref="deleteDialog"
                    v-bind:cancel-button-label="$t('cancel')"
                    v-bind:confirm-button-label="$t('delete')"
                    v-bind:message="$t('confirmDelete', { title: pendingDelete != null ? pendingDelete.title : '' })"
                    v-bind:title="$t('deleteDocument')"
                    v-model="deleteDialog" />

    <!-- Titel/Beschreibung/Sichtbarkeit bearbeiten -->
    <v-dialog v-model="editDialog"
              width="520">
      <v-card v-if="edit != null">
        <v-card-title>{{ $t('editDocument') }}</v-card-title>
        <v-card-text>
          <v-text-field counter="120"
                        hide-details="auto"
                        outlined
                        v-bind:label="$t('title')"
                        v-model="edit.title" />
          <v-text-field class="mt-4"
                        counter="200"
                        hide-details="auto"
                        outlined
                        v-bind:hint="$t('descriptionHint')"
                        v-bind:label="$t('descriptionOptional')"
                        v-model="edit.description" />
          <v-switch class="mt-4"
                    color="primary"
                    hide-details
                    v-bind:label="$t('visibleOnHome')"
                    v-model="edit.isPublished" />
          <v-alert class="mt-4 mb-0"
                   text
                   type="error"
                   v-if="editError != null">{{ editError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text
                 v-on:click="editDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary"
                 depressed
                 v-bind:disabled="edit.title == null || edit.title.trim().length === 0"
                 v-bind:loading="busy"
                 v-on:click="saveEdit">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- verdeckte Dateiauswahl für „Datei ersetzen“ -->
    <input accept="application/pdf,.pdf"
           class="d-none"
           ref="replaceInput"
           type="file"
           v-on:change="replaceFile" />

    <top-header narrow
                v-bind:subtitle="$t('subtitle')"
                v-bind:title="$t('infoDocuments')">
      <v-btn class="sf-back-btn"
             exact
             slot="left-button"
             text
             v-bind:to="{ name: 'administration-dashboard' }">
        <v-icon left>ti-arrow-left</v-icon>
        {{ $t('back') }}
      </v-btn>
    </top-header>

    <div class="sf-container sf-container--narrow">
      <!-- Hochladen -->
      <v-card class="sf-card"
              flat>
        <v-progress-linear color="accent"
                           indeterminate
                           v-bind:active="uploading" />
        <v-card-text class="sf-card__body">
          <h3 class="sf-section-title">{{ $t('upload') }}</h3>
          <p class="sf-muted mb-4">{{ $t('uploadHint') }}</p>
          <v-file-input accept="application/pdf,.pdf"
                        dense
                        hide-details="auto"
                        outlined
                        prepend-icon=""
                        prepend-inner-icon="ti-file-type-pdf"
                        show-size
                        v-bind:disabled="uploading"
                        v-bind:label="$t('pdfFile')"
                        v-model="uploadFile"
                        v-on:change="prefillTitle" />
          <v-text-field class="mt-4"
                        counter="120"
                        dense
                        hide-details="auto"
                        outlined
                        v-bind:disabled="uploading"
                        v-bind:label="$t('title')"
                        v-model="uploadTitle"
                        v-on:keyup.enter="upload" />
          <v-text-field class="mt-4"
                        counter="200"
                        dense
                        hide-details="auto"
                        outlined
                        v-bind:disabled="uploading"
                        v-bind:hint="$t('descriptionHint')"
                        v-bind:label="$t('descriptionOptional')"
                        v-model="uploadDescription"
                        v-on:keyup.enter="upload" />
          <v-alert class="mt-4 mb-0"
                   text
                   type="error"
                   v-if="uploadError != null">{{ uploadError }}</v-alert>
          <v-alert class="mt-4 mb-0"
                   text
                   type="success"
                   v-if="uploadSuccess != null">{{ $t('uploaded', { title: uploadSuccess }) }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="accent"
                 depressed
                 v-bind:disabled="!canUpload || uploading"
                 v-bind:loading="uploading"
                 v-on:click="upload">
            <v-icon left>ti-upload</v-icon>
            {{ $t('uploadButton') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Liste -->
      <v-card class="sf-card mt-6"
              flat>
        <v-progress-linear color="accent"
                           indeterminate
                           v-bind:active="loading || busy" />
        <v-card-text class="sf-card__body">
          <h3 class="sf-section-title">{{ $t('documents') }} <span class="sf-muted font-weight-regular">({{ infoDocuments.length }})</span></h3>
          <p class="sf-muted mb-0">{{ $t('listHint') }}</p>
          <v-alert class="mt-4 mb-0"
                   text
                   type="error"
                   v-if="listError != null">{{ listError }}</v-alert>
          <p class="mb-0 mt-4"
             v-if="!loading && infoDocuments.length === 0"><strong>{{ $t('empty') }}</strong> {{ $t('emptyHint') }}</p>
        </v-card-text>
        <template v-if="infoDocuments.length > 0">
          <v-divider />
          <v-list class="py-0 sf-docs"
                  two-line>
            <template v-for="(document, index) in infoDocuments">
              <v-list-item v-bind:key="document.id"
                           v-bind:class="{ 'sf-docs__item--hidden': !document.isPublished }">
                <v-list-item-icon class="sf-docs__icon">
                  <v-icon color="accent"
                          large>ti-file-type-pdf</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ document.title }}
                    <v-chip class="ml-2 sf-pill sf-pill--neutral"
                            x-small
                            v-if="!document.isPublished">
                      <v-icon left
                              x-small>ti-eye-off</v-icon>
                      {{ $t('hidden') }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="document.description != null && document.description.length > 0">{{ document.description }} · </span>
                    <span>{{ document.fileName }} · {{ document.size | formatFileSize }} · {{ document.modified | formatDate('L LT') }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="sf-docs__actions">
                  <v-btn icon
                         small
                         v-bind:disabled="busy || index === 0"
                         v-bind:title="$t('moveUp')"
                         v-on:click="move(index, -1)">
                    <v-icon small>ti-arrow-up</v-icon>
                  </v-btn>
                  <v-btn icon
                         small
                         v-bind:disabled="busy || index === infoDocuments.length - 1"
                         v-bind:title="$t('moveDown')"
                         v-on:click="move(index, 1)">
                    <v-icon small>ti-arrow-down</v-icon>
                  </v-btn>
                  <v-switch class="sf-docs__switch ml-1 mt-0 pt-0"
                            color="primary"
                            dense
                            hide-details
                            v-bind:disabled="busy"
                            v-bind:input-value="document.isPublished"
                            v-bind:title="$t('visibleOnHome')"
                            v-on:change="togglePublished(document, $event)" />
                  <v-menu left
                          offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn icon
                             small
                             v-bind:title="$t('actions')"
                             v-on="on">
                        <v-icon small>ti-dots</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item rel="noopener"
                                   target="_blank"
                                   v-bind:href="document.url">
                        <v-list-item-icon class="mr-3"><v-icon>ti-external-link</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('open') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-on:click="openEdit(document)">
                        <v-list-item-icon class="mr-3"><v-icon>ti-pencil</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-on:click="chooseReplacement(document)">
                        <v-list-item-icon class="mr-3"><v-icon>ti-file-upload</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('replaceFile') }}</v-list-item-title>
                      </v-list-item>
                      <v-divider />
                      <v-list-item v-on:click="remove(document)">
                        <v-list-item-icon class="mr-3"><v-icon color="error">ti-trash</v-icon></v-list-item-icon>
                        <v-list-item-title class="error--text">{{ $t('delete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-bind:key="`divider-${document.id}`"
                         v-if="index < infoDocuments.length - 1" />
            </template>
          </v-list>
        </template>
      </v-card>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import ConfirmDialog from '@/components/common/ConfirmDialog'
  import TopHeader from '@/components/common/TopHeader'

  export default {
    components: {
      ConfirmDialog,
      TopHeader
    },
    computed: {
      canUpload() {
        return this.uploadFile != null && this.uploadTitle != null && this.uploadTitle.trim().length > 0
      },
      ...mapGetters([ 'infoDocuments' ])
    },
    async created() {
      await this.load()
    },
    data: () => ({
      busy: false,
      deleteDialog: false,
      edit: null,
      editDialog: false,
      editError: null,
      listError: null,
      loading: false,
      pendingDelete: null,
      replaceTarget: null,
      uploadDescription: null,
      uploadError: null,
      uploadFile: null,
      uploadSuccess: null,
      uploadTitle: null,
      uploading: false
    }),
    methods: {
      async load() {
        this.loading = true
        this.listError = null

        try {
          await this.$store.dispatch('loadAllInfoDocuments')
        } catch (e) {
          this.listError = e.message
        } finally {
          this.loading = false
        }
      },
      /** Titel aus dem Dateinamen vorbelegen (ohne Endung, Striche/Unterstriche als Leerzeichen), solange noch kein Titel eingegeben wurde. */
      prefillTitle(file) {
        if (file == null || (this.uploadTitle != null && this.uploadTitle.trim().length > 0)) {
          return
        }

        this.uploadTitle = file.name.replace(/\.pdf$/i, '').replace(/[-_]+/g, ' ').trim()
      },
      async upload() {
        if (!this.canUpload || this.uploading) {
          return
        }

        this.uploading = true
        this.uploadError = null
        this.uploadSuccess = null

        try {
          const infoDocument = await this.$store.dispatch('createInfoDocument', {
            title: this.uploadTitle.trim(),
            description: this.uploadDescription,
            file: this.uploadFile
          })

          this.uploadSuccess = infoDocument.title
          this.uploadFile = null
          this.uploadTitle = null
          this.uploadDescription = null
        } catch (e) {
          this.uploadError = e.message
        } finally {
          this.uploading = false
        }
      },
      async move(index, offset) {
        const target = index + offset

        if (target < 0 || target >= this.infoDocuments.length) {
          return
        }

        const ids = this.infoDocuments.map(d => d.id)
        const [ id ] = ids.splice(index, 1)
        ids.splice(target, 0, id)

        await this.run(() => this.$store.dispatch('reorderInfoDocuments', { ids }))
      },
      async togglePublished(document, isPublished) {
        await this.run(() => this.$store.dispatch('updateInfoDocument', {
          id: document.id,
          title: document.title,
          description: document.description,
          isPublished: !!isPublished
        }))
      },
      openEdit(document) {
        this.edit = {
          id: document.id,
          title: document.title,
          description: document.description,
          isPublished: document.isPublished
        }
        this.editError = null
        this.editDialog = true
      },
      async saveEdit() {
        if (this.edit == null) {
          return
        }

        this.busy = true
        this.editError = null

        try {
          await this.$store.dispatch('updateInfoDocument', {
            id: this.edit.id,
            title: this.edit.title.trim(),
            description: this.edit.description,
            isPublished: this.edit.isPublished
          })

          this.editDialog = false
        } catch (e) {
          this.editError = e.message
        } finally {
          this.busy = false
        }
      },
      chooseReplacement(document) {
        this.replaceTarget = document
        this.$refs.replaceInput.value = ''
        this.$refs.replaceInput.click()
      },
      async replaceFile(event) {
        const file = event.target.files != null ? event.target.files[0] : null
        const target = this.replaceTarget

        this.replaceTarget = null

        if (file == null || target == null) {
          return
        }

        await this.run(() => this.$store.dispatch('replaceInfoDocumentFile', { id: target.id, file }))
      },
      async remove(document) {
        this.pendingDelete = document

        const confirmed = await new Promise(resolve => this.$refs.deleteDialog.open(resolve))

        if (!confirmed) {
          return
        }

        await this.run(() => this.$store.dispatch('deleteInfoDocument', { id: document.id }))
      },
      /** Führt eine Listenaktion aus, zeigt Fehler über der Liste und lädt bei Fehlern den Serverstand neu. */
      async run(action) {
        this.busy = true
        this.listError = null

        try {
          await action()
        } catch (e) {
          this.listError = e.message

          try {
            await this.$store.dispatch('loadAllInfoDocuments')
          } catch (reloadError) {
            // Fehlermeldung der eigentlichen Aktion bleibt stehen
          }
        } finally {
          this.busy = false
        }
      }
    },
    name: 'InfoDocuments'
  }
</script>

<style scoped>
  .sf-docs__icon {
    align-self: center;
    margin-right: 16px !important;
  }

  .sf-docs__item--hidden .v-list-item__title,
  .sf-docs__item--hidden .sf-docs__icon .v-icon {
    opacity: .6;
  }

  .sf-docs__actions {
    align-items: center;
    align-self: center;
    flex-direction: row;
    gap: 2px;
    margin: 0 0 0 8px !important;
  }

  .sf-docs__switch {
    margin-right: 4px;
  }

  .sf-docs__switch >>> .v-input__slot {
    margin-bottom: 0;
  }

  @media (max-width: 599px) {
    .sf-docs__actions {
      flex-wrap: wrap;
      justify-content: flex-end;
      max-width: 112px;
    }
  }
</style>

<i18n>
  {
    "de": {
      "infoDocuments": "Vorabinformationen",
      "subtitle": "PDF-Dokumente, die Kunden auf der Startseite unter „Vorabinformationen“ sehen.",
      "back": "Zurück",
      "upload": "Dokument hochladen",
      "uploadHint": "Nur PDF, bis 20 MB. Der Titel erscheint auf der Kachel; die Beschreibung ersetzt die Unterzeile „PDF · Größe“.",
      "pdfFile": "PDF-Datei",
      "title": "Titel",
      "descriptionOptional": "Beschreibung (optional)",
      "descriptionHint": "Z. B. „Stand März 2027“ oder „Gültig für alle Filialen“",
      "uploadButton": "Hochladen",
      "uploaded": "„{title}“ wurde hochgeladen und ist auf der Startseite sichtbar.",
      "documents": "Dokumente",
      "listHint": "Die Reihenfolge entspricht der Anzeige auf der Startseite. Ausgeblendete Dokumente sehen nur Administratoren.",
      "empty": "Noch keine Dokumente.",
      "emptyHint": "Kunden sehen auf der Startseite den Hinweis „Vorabinformationen werden in Kürze bereitgestellt.“",
      "hidden": "Ausgeblendet",
      "moveUp": "Nach oben",
      "moveDown": "Nach unten",
      "visibleOnHome": "Auf der Startseite sichtbar",
      "actions": "Aktionen",
      "open": "Öffnen",
      "edit": "Bearbeiten",
      "editDocument": "Dokument bearbeiten",
      "replaceFile": "Datei ersetzen",
      "delete": "Löschen",
      "deleteDocument": "Dokument löschen",
      "confirmDelete": "Soll „{title}“ endgültig gelöscht werden? Die Datei wird vom Server entfernt.",
      "cancel": "Abbrechen",
      "save": "Speichern"
    },
    "en": {
      "infoDocuments": "Preliminary information",
      "subtitle": "PDF documents customers see on the home page under “Preliminary information”.",
      "back": "Back",
      "upload": "Upload document",
      "uploadHint": "PDF only, up to 20 MB. The title is shown on the tile; the description replaces the “PDF · size” subline.",
      "pdfFile": "PDF file",
      "title": "Title",
      "descriptionOptional": "Description (optional)",
      "descriptionHint": "E.g. “As of March 2027” or “Valid for all branches”",
      "uploadButton": "Upload",
      "uploaded": "“{title}” has been uploaded and is visible on the home page.",
      "documents": "Documents",
      "listHint": "The order matches the home page. Hidden documents are only visible to administrators.",
      "empty": "No documents yet.",
      "emptyHint": "Customers see the note “Preliminary information will be provided shortly.” on the home page.",
      "hidden": "Hidden",
      "moveUp": "Move up",
      "moveDown": "Move down",
      "visibleOnHome": "Visible on the home page",
      "actions": "Actions",
      "open": "Open",
      "edit": "Edit",
      "editDocument": "Edit document",
      "replaceFile": "Replace file",
      "delete": "Delete",
      "deleteDocument": "Delete document",
      "confirmDelete": "Delete “{title}” permanently? The file will be removed from the server.",
      "cancel": "Cancel",
      "save": "Save"
    }
  }
</i18n>
